import { useState, useRef } from "react";
import { uploadImage, resolveImageUrl, ApiError } from "@/lib/api";

interface ImageUploaderProps {
  imageUrls: string[];
  onChange: (urls: string[]) => void;
  maxImages?: number;
}

export default function ImageUploader({ imageUrls, onChange, maxImages = 5 }: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadFile = async (file: File): Promise<{ url: string | null; error: string }> => {
    try {
      const url = await uploadImage(file);
      return { url, error: "" };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "이미지 업로드에 실패했습니다.";
      return { url: null, error: message };
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadError("");
    setUploading(true);

    const remaining = maxImages - imageUrls.length;
    const filesToUpload = Array.from(files).slice(0, remaining);

    if (files.length > remaining) {
      setUploadError(`최대 ${maxImages}장까지 업로드 가능합니다.`);
    }

    const newUrls: string[] = [];
    for (const file of filesToUpload) {
      if (!file.type.startsWith("image/")) {
        setUploadError("이미지 파일만 업로드 가능합니다.");
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("5MB 이하의 이미지만 업로드 가능합니다.");
        continue;
      }

      const result = await uploadFile(file);
      if (result.url) {
        newUrls.push(result.url);
      } else {
        setUploadError(result.error);
      }
    }

    if (newUrls.length > 0) {
      onChange([...imageUrls, ...newUrls]);
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemove = (index: number) => {
    const updated = imageUrls.filter((_, i) => i !== index);
    onChange(updated);
    if (previewIndex === index) {
      setPreviewIndex(null);
    } else if (previewIndex !== null && previewIndex > index) {
      setPreviewIndex(previewIndex - 1);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          현장 사진 <span className="text-gray-400 font-normal">(최대 {maxImages}장)</span>
        </label>
        <span className="text-xs text-gray-400">{imageUrls.length}/{maxImages}</span>
      </div>

      <div className="flex flex-wrap gap-3">
        {imageUrls.map((url, index) => (
          <div key={index} className="relative group">
            <div
              className="w-24 h-24 rounded-lg overflow-hidden border border-gray-200 cursor-pointer"
              onClick={() => setPreviewIndex(index)}
            >
              <img
                src={resolveImageUrl(url)}
                alt={`현장사진 ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(index);
              }}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        ))}

        {imageUrls.length < maxImages && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-amber-400 hover:text-amber-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <>
                <i className="ri-camera-line text-lg"></i>
                <span className="text-xs">사진 추가</span>
              </>
            )}
          </button>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
          <i className="ri-error-warning-line"></i>
          {uploadError}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {previewIndex !== null && imageUrls[previewIndex] && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setPreviewIndex(null)}
        >
          <div className="relative max-w-3xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveImageUrl(imageUrls[previewIndex])}
              alt={`현장사진 확대 ${previewIndex + 1}`}
              className="max-w-full max-h-[85vh] rounded-xl object-contain"
            />
            <button
              onClick={() => setPreviewIndex(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>
            <div className="flex items-center justify-center gap-2 mt-3">
              {imageUrls.length > 1 && (
                <>
                  <button
                    onClick={() => setPreviewIndex(prev => (prev !== null && prev > 0 ? prev - 1 : imageUrls.length - 1))}
                    className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-gray-700"></i>
                  </button>
                  <span className="text-white text-sm">{previewIndex + 1} / {imageUrls.length}</span>
                  <button
                    onClick={() => setPreviewIndex(prev => (prev !== null && prev < imageUrls.length - 1 ? prev + 1 : 0))}
                    className="w-8 h-8 bg-white/80 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-gray-700"></i>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}